import { useEffect, useState, useCallback } from "react";
import { messaging, VAPID_KEY } from "../config/firebase";
import { getToken, onMessage } from "firebase/messaging";
import { playNotificationSound, chatPageHandlesSound } from "../utils/notificationSound";
import { axiosPrivate } from "../api/axios";
import logger from "../utils/logger";

/**
 * Hook to manage Firebase Cloud Messaging (FCM) notifications
 * Handles token registration, foreground notifications, and permission requests
 */
export const useFirebaseNotifications = (userId) => {
  const [fcmToken, setFcmToken] = useState(null);
  const [notificationPermission, setNotificationPermission] =
    useState("default");
  const [isTokenRegistered, setIsTokenRegistered] = useState(false);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      logger.warn("This browser does not support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      setNotificationPermission("granted");
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === "granted";
    }

    setNotificationPermission("denied");
    return false;
  }, []);

  // Register service worker for background notifications
  const registerServiceWorker = useCallback(async () => {
    if ("serviceWorker" in navigator) {
      try {
        // Check if service worker is already registered
        let registration = await navigator.serviceWorker.getRegistration(
          "/firebase-messaging-sw.js",
        );

        if (!registration) {
          registration = await navigator.serviceWorker.register(
            "/firebase-messaging-sw.js",
            {
              scope: "/",
            },
          );
          logger.log("Service Worker registered");
        } else {
          logger.log("Service Worker already registered");
        }

        // Wait for service worker to be ready
        if (registration.installing) {
          await new Promise((resolve) => {
            registration.installing.addEventListener(
              "statechange",
              function () {
                if (this.state === "installed") {
                  resolve();
                }
              },
            );
          });
        }

        // Ensure service worker is active
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }

        return registration;
      } catch (error) {
        logger.error("Service Worker registration failed:", error);
        return null;
      }
    }
    return null;
  }, []);

  // Get FCM token
  const getFCMToken = useCallback(async () => {
    if (!messaging) {
      logger.warn("Firebase messaging not initialized");
      return null;
    }

    try {
      // Register service worker first and wait for it to be ready
      const registration = await registerServiceWorker();
      if (!registration) {
        logger.warn("Service worker registration failed");
        return null;
      }

      // Wait for service worker to be active
      if (registration.active) {
        // Service worker is already active
      } else if (registration.installing) {
        await new Promise((resolve) => {
          registration.installing.addEventListener("statechange", function () {
            if (this.state === "activated") {
              resolve();
            }
          });
        });
      } else if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Validate VAPID key format
      if (!VAPID_KEY || VAPID_KEY.length < 80) {
        logger.error(
          "Invalid VAPID key. Please check Firebase Console → Cloud Messaging → Web configuration",
        );
        return null;
      }

      // Get token with VAPID key
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        logger.log("FCM token retrieved successfully");
        setFcmToken(token);
        return token;
      } else {
        logger.warn(
          "No registration token available. Check permission, VAPID key, and Cloud Messaging API.",
        );
        return null;
      }
    } catch (error) {
      logger.error("Error retrieving FCM token:", error?.message || error);
      if (
        error.message?.includes("push service error") ||
        error.message?.includes("AbortError")
      ) {
        const hostname = window.location.hostname;
        const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
        const isHTTPS = window.location.protocol === "https:";
        const isLocalhost =
          hostname === "localhost" || hostname === "127.0.0.1";
        if (isIP && !isHTTPS && !isLocalhost) {
          logger.error(
            "FCM requires HTTPS or localhost. Use localhost or add domain in Firebase Console.",
          );
        }
      }
      return null;
    }
  }, [registerServiceWorker]);

  // Send token to backend (backend uses JWT req.userId; we only need token in body)
  const registerToken = useCallback(
    async (token) => {
      if (!token || !userId) {
        if (!userId)
          logger.warn("FCM: Skipping token registration — no userId");
        return;
      }

      try {
        await axiosPrivate.post("/api/notifications/register-token", { token });
        setIsTokenRegistered(true);
        logger.log("FCM token registered successfully");
      } catch (error) {
        logger.error(
          "Failed to register FCM token:",
          error?.response?.data || error.message,
        );
        setIsTokenRegistered(false);
        if (error?.response?.status === 404) {
          logger.warn("FCM: Employee not found. Ensure you are logged in.");
        }
      }
    },
    [userId],
  );

  // Initialize: Request permission and get token
  const initializeNotifications = useCallback(async () => {
    if (!userId) {
      logger.warn("FCM: Cannot initialize — no userId");
      return;
    }

    const hasPermission = await requestPermission();
    if (hasPermission) {
      const token = await getFCMToken();
      if (token) {
        await registerToken(token);
      } else {
        // Token generation failed - check if it's due to IP/HTTP issue
        const hostname = window.location.hostname;
        const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
        const isHTTPS = window.location.protocol === "https:";
        const isLocalhost =
          hostname === "localhost" || hostname === "127.0.0.1";

        if (isIP && !isHTTPS && !isLocalhost) {
          logger.error("FCM token failed: use HTTPS or localhost.");
        } else {
          logger.warn("FCM token generation failed.");
        }
      }
    }
  }, [requestPermission, getFCMToken, registerToken, userId]);

  // Tab open but in the background: the service worker receives the push and asks the page to ring.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onSwMessage = (event) => {
      if (event.data?.type === "fcm-notification-sound" && !chatPageHandlesSound()) playNotificationSound();
    };
    navigator.serviceWorker.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onSwMessage);
  }, []);

  // Handle foreground messages
  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      logger.log("Message received in foreground");

      const title = payload.notification?.title || "New Message";
      const body = payload.notification?.body || "";
      const data = payload.data || {};
      const type = data.type;

      // Sound while the app is open and focused.
      if (!chatPageHandlesSound()) playNotificationSound();

      // Show browser notification (may not appear when tab is focused in some browsers)
      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        try {
          const notificationOptions = {
            body,
            icon: payload.notification?.icon || "/favicon.ico",
            badge: "/favicon.ico",
            tag: data.channelId || "notification",
            data: {
              url: data.url || "/communication/chat",
              channelId: data.channelId,
            },
            requireInteraction: false,
          };
          const n = new Notification(title, notificationOptions);
          n.onclick = () => {
            window.focus();
            if (data.url) window.location.href = data.url;
            n.close();
          };
        } catch (err) {
          logger.warn("Could not show browser notification:", err);
        }
      }

      // Dispatch custom event so Layout can show an in-app toast
      window.dispatchEvent(
        new CustomEvent("firebase-foreground-notification", {
          detail: { title, body, data },
        }),
      );

      // Special event for channel creation so chat UI can refresh list
      if (type === "channel_created" && data.channelId) {
        window.dispatchEvent(
          new CustomEvent("chat-channel-created", {
            detail: { channelId: data.channelId, title, body },
          }),
        );
      }

      // Ticket assigned – Assigned To You page updates in real time via Firebase
      if (type === "ticket_assigned" && data.ticketId) {
        try {
          const ticketData = data.ticketData
            ? JSON.parse(data.ticketData)
            : null;
          window.dispatchEvent(
            new CustomEvent("ticket-assigned", {
              detail: {
                ticketId: data.ticketId,
                assignedBy: data.assignedBy,
                assignedByName: data.assignedByName,
                ticket: ticketData,
                ticketData: ticketData,
                title,
                body,
                url: data.url || "/communication/tickets",
              },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched ticket-assigned");
        } catch (parseError) {
          logger.error(
            "Error parsing ticket data from Firebase notification:",
            parseError,
          );
        }
      }

      // Ticket message – notify participants so ticket conversation updates in real time
      if (type === "ticket_message" && data.ticketId) {
        try {
          const ticketData = data.ticketData
            ? JSON.parse(data.ticketData)
            : null;
          window.dispatchEvent(
            new CustomEvent("ticket-message", {
              detail: {
                ticketId: data.ticketId,
                messageId: data.messageId,
                sentBy: data.sentBy,
                sentByName: data.sentByName || "Someone",
                ticket: ticketData,
                ticketData: ticketData,
                title,
                body,
                url: data.url || "/tickets",
              },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched ticket-message");
        } catch (parseError) {
          logger.error(
            "Error parsing ticket message data from Firebase notification:",
            parseError,
          );
        }
      }

      // Chat / channel: added as member – Assigned To You page and chat list update in real time via Firebase
      if (type === "chat_assigned" && data.channelId) {
        try {
          window.dispatchEvent(
            new CustomEvent("chat-assigned", {
              detail: {
                channelId: data.channelId,
                channelName: data.channelName || "",
                addedBy: data.addedBy,
                addedByName: data.addedByName || "Someone",
                title,
                body,
                url: data.url || "/communication/chat",
              },
            }),
          );
          // Also refresh chat channel list so the new channel appears in the sidebar
          window.dispatchEvent(
            new CustomEvent("chat-channel-created", {
              detail: { channelId: data.channelId, title, body },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched chat-assigned");
        } catch (parseError) {
          logger.error(
            "Error parsing chat-assigned data from Firebase notification:",
            parseError,
          );
        }
      }

      // Expense added – notify debtor so Expenses page updates in real time
      if (type === "expense_added" && data.debtorId) {
        try {
          window.dispatchEvent(
            new CustomEvent("expense-added", {
              detail: {
                expenseId: data.expenseId,
                debtorId: data.debtorId,
                creditorId: data.creditorId,
                amount: parseFloat(data.amount) || 0,
                title,
                body,
                url: data.url || "/communication/expenses",
              },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched expense-added");
        } catch (parseError) {
          logger.error(
            "Error parsing expense-added data from Firebase notification:",
            parseError,
          );
        }
      }

      // Payment claimed – notify creditor so Expenses page updates in real time
      if (type === "payment_claimed" && data.creditorId) {
        try {
          window.dispatchEvent(
            new CustomEvent("payment-claimed", {
              detail: {
                debtId: data.debtId,
                expenseId: data.expenseId,
                debtorId: data.debtorId,
                creditorId: data.creditorId,
                amount: parseFloat(data.amount) || 0,
                title,
                body,
                url: data.url || "/communication/expenses",
              },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched payment-claimed");
        } catch (parseError) {
          logger.error(
            "Error parsing payment-claimed data from Firebase notification:",
            parseError,
          );
        }
      }

      // Payment confirmed – notify debtor so Expenses page updates in real time
      if (type === "payment_confirmed" && data.debtorId) {
        try {
          window.dispatchEvent(
            new CustomEvent("payment-confirmed", {
              detail: {
                debtId: data.debtId,
                expenseId: data.expenseId,
                debtorId: data.debtorId,
                creditorId: data.creditorId,
                amount: parseFloat(data.amount) || 0,
                title,
                body,
                url: data.url || "/communication/expenses",
              },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched payment-confirmed");
        } catch (parseError) {
          logger.error(
            "Error parsing payment-confirmed data from Firebase notification:",
            parseError,
          );
        }
      }

      // Meeting note – user added as viewer/editor; notify so Meeting Notes list updates in real time
      if (type === "meeting_note_added" && data.noteId) {
        try {
          window.dispatchEvent(
            new CustomEvent("meeting-note-added", {
              detail: {
                noteId: data.noteId,
                noteTitle: data.noteTitle || "Meeting note",
                addedBy: data.addedBy,
                addedByName: data.addedByName || "Someone",
                title,
                body,
                url: data.url || "/communication/meeting-notes",
              },
            }),
          );
          logger.log(
            "[useFirebaseNotifications] Dispatched meeting-note-added",
          );
        } catch (parseError) {
          logger.error(
            "Error parsing meeting-note-added data from Firebase notification:",
            parseError,
          );
        }
      }

      // Payment rejected – notify debtor so Expenses page updates in real time
      if (type === "payment_rejected" && data.debtorId) {
        try {
          window.dispatchEvent(
            new CustomEvent("payment-rejected", {
              detail: {
                debtId: data.debtId,
                expenseId: data.expenseId,
                debtorId: data.debtorId,
                creditorId: data.creditorId,
                amount: parseFloat(data.amount) || 0,
                title,
                body,
                url: data.url || "/communication/expenses",
              },
            }),
          );
          logger.log("[useFirebaseNotifications] Dispatched payment-rejected");
        } catch (parseError) {
          logger.error(
            "Error parsing payment-rejected data from Firebase notification:",
            parseError,
          );
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Check notification permission status
  useEffect(() => {
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  return {
    fcmToken,
    notificationPermission,
    isTokenRegistered,
    requestPermission,
    getFCMToken,
    registerToken,
    initializeNotifications,
  };
};

export default useFirebaseNotifications;
